import {useContext, useEffect, useState} from 'react';
import {motion} from 'framer-motion';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import Pagination from '@mui/material/Pagination';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import AuthContext from '../../contexts/auth-context';
import AppContext from '../../contexts/app-context';
import useAxios from '../../hooks/useAxios';
import {PAGE_SIZE, urls, app_paths, patientFormType} from '../../components/config/variables';
import PatientCard from '../../components/users/PatientCard/PatientCard';
import PatientsPageStyle from './PatientsPage.module';
import { pageTransition, pageVariants } from '../../constants/GlobalStyles';
import { NavLink } from 'react-router-dom';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import Divider from '@mui/material/Divider';

const PatientsPage = () => {
    const classes = PatientsPageStyle();
    const authCtx = useContext(AuthContext);
    const appCtx = useContext(AppContext);
    const userRole = appCtx.userRole;
    const { operation } = useAxios();

    const [searchQuery, setSearchQuery] = useState('');
    const [isSearch, setIsSearch] = useState(false);
    const [patients, setUsersList] = useState([]);
    const [loading, setLoadingUsersList] = useState(false);
    const [error, setUsersListError] = useState();
    const [count, setCount] = useState(1);
    const [page, setPage] = useState(1);
    const [totalPatients, setTotalPatients] = useState(0);
    
      const setFilteredUserList = (users) => {
        setLoadingUsersList(false);
        setUsersList(users.results);
        setTotalPatients(users.count)
        setCount(Math.ceil(users.count / PAGE_SIZE));
        appCtx.addUsersList(users);
      };
    
      const getUsersList = (filter) => {
        setLoadingUsersList(true);
        operation(
          {
            method: 'GET',
            url: urls.LIST_PATIENTS + filter,
            headers: { Authorization: `token ${authCtx.token}` },
            data: {},
          },
          setFilteredUserList,
          handleUsersListError
        );
      };
    
      const handleUsersListError = (error) => {
        setLoadingUsersList(false);
        setUsersListError(error);
      };
    
      useEffect(() => {
        if (!appCtx.reFetchUsersList) {
          const searchForPatient = `?searchfor=${searchQuery}`;
          const filter = searchQuery
            ? `${searchForPatient}&page=${page}&page_size=${PAGE_SIZE}`
            : `?page=${page}&page_size=${PAGE_SIZE}`;
          getUsersList(filter);
        }
        // eslint-disable-next-line
      }, [page]);
    
      const handleSearchClick = (event) => {
        if (searchQuery !== '') {
          event.preventDefault();
          setIsSearch(true);
          setPage(1);
          getUsersList(`?searchfor=${searchQuery}&page=1&page_size=${PAGE_SIZE}`);
        }
      };
    
      const handleClearSearch = () => {
        setIsSearch(false);
        setSearchQuery('');
        setPage(1);
        getUsersList(`?page=1&page_size=${PAGE_SIZE}`);
      };
    
      useEffect(() => {
        if (isSearch && searchQuery === '') {
          handleClearSearch();
        }
        // eslint-disable-next-line
      }, [searchQuery, isSearch]);
    
      useEffect(() => {
        if (appCtx.reFetchUsersList) {
          if (appCtx.reFetchUsersList === patientFormType.create) {
            getUsersList(`?page=1&page_size=${PAGE_SIZE}`);
            setPage(1);
          } else {
            getUsersList(`?page=${page}&page_size=${PAGE_SIZE}`);
          }
        }
        // eslint-disable-next-line
      }, [appCtx.reFetchUsersList]);

    return (
        <motion.div
            variants={pageVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            transition={pageTransition}
        >
            <Box>
                <Box
                    sx={classes.topBar}
                >
                    <Box sx={classes.topBarLeft}>
                        
                        {/* Total Patients */}
                        <Box
                            sx={classes.totalPatientBox}
                        >
                            <Typography
                                sx={classes.totalPatientLabel}
                            >
                                Total Patients
                            </Typography>

                            {loading
                             ? <CircularProgress size={18} />
                             :<Typography
                                sx={classes.totalPatientValue}
                            >
                                {totalPatients}
                            </Typography>}
                        </Box>

                        <Divider orientation="vertical" flexItem />

                        {/* Search */}
                        <TextField
                            label="Search"
                            variant="outlined"
                            size="small"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            onKeyDown={(event) => event.key === "Enter" && handleSearchClick(event)}
                            InputProps={{
                                startAdornment: searchQuery && (
                                    <InputAdornment position="start">
                                        <IconButton size="small" onClick={handleClearSearch}>
                                            <ClearIcon fontSize="small" />
                                        </IconButton>
                                    </InputAdornment>
                                ),
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton onClick={handleSearchClick}>
                                            <SearchIcon />
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Box>

                    {/* Add Doctor Button */}
                    <Box sx={classes.topBarRight}>
                        <Divider orientation="vertical" flexItem />
                        {/* Add Patient Button */}
                    {userRole === 'doctor' && <NavLink
                        to={app_paths.NEW_PATIENT}
                        style={classes.navLink}
                    >
                        <Box
                                sx={classes.newDoctorButton}
                            >
                                <AddCircleOutlineIcon />
                                <Typography fontWeight={600}>New Patient</Typography>
                            </Box>
                    </NavLink>}
                    </Box>
                </Box>

                {/* Error State */}
                {error && (
                    <Alert severity="error" sx={classes.errorAlert}>
                        Failed to load patients. Please try again.
                    </Alert>
                )}

                {/* Loading State */}
                {loading && (
                    <Box sx={classes.loadingContainer}>
                        <CircularProgress size={48}/>
                    </Box>
                )}

                {/* Patients Grid */}
                {!loading && patients.length > 0 && (
                    <Box sx={classes.patientsGrid}>
                        {patients.map((patient) => (
                            <PatientCard key={patient.id} patient={patient}/>
                        ))}
                    </Box>
                )}

                {/* Empty State */}
                {!loading && patients.length === 0 && (
                    <Box sx={classes.emptyState}>
                        <PeopleOutlineIcon sx={classes.emptyStateIcon}/>
                        <Typography sx={classes.emptyStateTitle}>
                            {searchQuery ? 'No patients found' : 'No patients yet'}
                        </Typography>
                        <Typography sx={classes.emptyStateText}>
                            {searchQuery
                                ? 'Try adjusting your search terms'
                                : 'Patients will appear here once they are added to the system'}
                        </Typography>
                    </Box>
                )}

                {/* Pagination */}
                {!loading && patients.length > 0 && count > 1 && (
                    <Box sx={classes.paginationContainer}>
                        <Pagination
                            count={count}
                            page={page}
                            onChange={(e, v) => setPage(v)}
                            color="primary"
                            size="large"
                        />
                    </Box>
                )}
            </Box>
        </motion.div>
    );
};

export default PatientsPage;
